import React, { useEffect, useState, useCallback } from 'react';
import type { ClassRoom, GradeLevelEntity, Major, Branch, Student } from '../../types/db';
import { Plus, Trash2, X, Loader2, Save, Layers, Building2, LayoutGrid, Info, Users, UserPlus, UserMinus } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

export function ClassManager({ branchId }: { branchId?: string }) {
  const [classes, setClasses] = useState<ClassRoom[]>([]);
  const [grades, setGrades] = useState<GradeLevelEntity[]>([]);
  const [majors, setMajors] = useState<Major[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);

  // State untuk manajemen siswa dalam kelas
  const [selectedClass, setSelectedClass] = useState<ClassRoom | null>(null);
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  const [availableStudents, setAvailableStudents] = useState<Student[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // State kuota & rekap jumlah siswa per kelas { class_id: count }
  const [studentCounts, setStudentCounts] = useState<Record<string, number>>({});

  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<{
    branch_id: string;
    grade_level_id: string;
    major_id: string;
    parallel: number | null;
    name: string;
    max_capacity: number;
  }>({
    branch_id: branchId && branchId !== 'ALL' ? branchId : '',
    grade_level_id: '',
    major_id: '',
    parallel: null,
    name: '',
    max_capacity: 15,
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('classes')
        .select('*, grade_level:grade_levels(*), major:majors(*)');
      if (branchId && branchId !== 'ALL') query = query.eq('branch_id', branchId);

      const { data: cData } = await query.order('name');
      if (cData) setClasses(cData);

      const [gRes, mRes, bRes, sRes] = await Promise.all([
        supabase.from('grade_levels').select('*').order('id'),
        supabase.from('majors').select('*').order('name'),
        supabase.from('branches').select('*'),
        supabase.from('students').select('id, class_id'),
      ]);

      if (gRes.data) setGrades(gRes.data);
      if (mRes.data) setMajors(mRes.data);
      if (bRes.data) setBranches(bRes.data.filter((b) => b.id !== 'HQ' && b.id !== 'HEADQUARTERS'));

      // Hitung jumlah siswa per kelas
      if (sRes.data) {
        const counts: Record<string, number> = {};
        sRes.data.forEach((st) => {
          if (st.class_id) {
            counts[st.class_id] = (counts[st.class_id] || 0) + 1;
          }
        });
        setStudentCounts(counts);
      }
    } catch (err: any) {
      /* console removed */
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Kelola Siswa di Modal
  const openStudentModal = async (cls: ClassRoom) => {
    setSelectedClass(cls);
    setLoadingStudents(true);
    try {
      // 1. Ambil siswa yang saat ini di kelas ini
      const { data: currentS } = await supabase
        .from('students')
        .select('*')
        .eq('class_id', cls.id)
        .order('name');

      // 2. Ambil siswa yang belum punya kelas di cabang yang sama
      const { data: unassignedS } = await supabase
        .from('students')
        .select('*')
        .eq('branch_id', cls.branch_id)
        .is('class_id', null)
        .order('name');

      setClassStudents(currentS || []);
      setAvailableStudents(unassignedS || []);
    } catch (err: any) {
      window.alert('Gagal mengambil data siswa: ' + err.message);
    } finally {
      setLoadingStudents(false);
    }
  };

  const assignStudentToClass = async (studentId: string) => {
    if (!selectedClass) return;
    const maxCap = selectedClass.max_capacity || 15;

    if (classStudents.length >= maxCap) {
      return window.alert(`Kelas ini sudah penuh! Maksimal ${maxCap} siswa.`);
    }

    try {
      const { error } = await supabase
        .from('students')
        .update({ class_id: selectedClass.id })
        .eq('id', studentId);

      if (error) throw error;
      
      // Refresh modal & list
      await openStudentModal(selectedClass);
      loadData();
    } catch (err: any) {
      window.alert('Gagal memasukkan siswa ke kelas: ' + err.message);
    }
  };

  const removeStudentFromClass = async (studentId: string) => {
    if (!selectedClass) return;
    try {
      const { error } = await supabase
        .from('students')
        .update({ class_id: null })
        .eq('id', studentId);

      if (error) throw error;

      await openStudentModal(selectedClass);
      loadData();
    } catch (err: any) {
      window.alert('Gagal mengeluarkan siswa: ' + err.message);
    }
  };

  useEffect(() => {
    const grade = grades.find((g) => g.id === formData.grade_level_id)?.level;
    if (!grade) return;

    const major = majors.find((m) => m.id === formData.major_id)?.code;
    const parallel = formData.parallel ? ` ${formData.parallel}` : '';

    let name = `Kelas ${grade}`;
    if (major) name += ` ${major}`;
    name += parallel;

    setFormData((prev) => ({ ...prev, name }));
  }, [formData.grade_level_id, formData.major_id, formData.parallel, grades, majors]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.branch_id || !formData.grade_level_id || !formData.name) {
      return window.alert('Mohon lengkapi data kelas!');
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('classes').insert({
        branch_id: formData.branch_id,
        grade_level_id: formData.grade_level_id,
        major_id: formData.major_id || null,
        parallel: formData.parallel,
        name: formData.name,
        max_capacity: formData.max_capacity || 15,
      });

      if (error) throw error;
      window.alert('Kelas berhasil dibuat!');
      setIsFormOpen(false);
      loadData();
    } catch (err: any) {
      window.alert('Gagal: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Hapus kelas ini? Siswa di kelas ini akan menjadi tanpa kelas.'))
      return;
    try {
      await supabase.from('students').update({ class_id: null }).eq('class_id', id);
      const { error } = await supabase.from('classes').delete().eq('id', id);
      if (error) throw error;
      loadData();
    } catch (err: any) {
      window.alert('Gagal menghapus: ' + err.message);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in font-sans">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-indigo-600 text-white rounded-2xl flex items-center justify-center shadow-xl shadow-indigo-100">
            <Layers className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight leading-none">
              Manajemen Kelas Paralel
            </h2>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
              Atur entitas kelas & kapasitas siswa per cabang
            </p>
          </div>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="w-full md:w-auto bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
        >
          <Plus className="w-4 h-4" /> Buat Kelas Baru
        </button>
      </div>

      <div className="bg-white rounded-[3rem] shadow-sm border border-slate-100 overflow-hidden relative">
        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] z-10 flex items-center justify-center">
            <Loader2 className="animate-spin text-indigo-600 w-10 h-10" />
          </div>
        )}

        <div className="overflow-x-auto no-scrollbar">
          <table className="w-full text-sm text-slate-800 min-w-[800px]">
            <thead className="bg-slate-50 border-b text-slate-400 font-black uppercase tracking-widest text-[9px] md:text-[10px]">
              <tr>
                <th className="p-8 text-left">Nama Kelas</th>
                <th className="p-8 text-left">Jenjang & Jurusan</th>
                <th className="p-8 text-left">Kapasitas Siswa</th>
                <th className="p-8 text-left">Unit Cabang</th>
                <th className="p-8 text-center">Opsi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {classes.map((cls) => {
                const count = studentCounts[cls.id] || 0;
                const maxCap = cls.max_capacity || 15;
                const isFull = count >= maxCap;

                return (
                  <tr key={cls.id} className="hover:bg-indigo-50/20 transition-all font-bold group">
                    <td className="p-8">
                      <div className="text-indigo-600 font-black text-lg uppercase">{cls.name}</div>
                      <div className="text-[8px] text-slate-400 font-black uppercase tracking-widest mt-1">
                        Paralel: {cls.parallel || 'Reguler'}
                      </div>
                    </td>
                    <td className="p-8">
                      <div className="uppercase tracking-tight text-slate-900">
                        {cls.grade_level?.level ? `Kelas ${cls.grade_level.level}` : '-'}
                      </div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-widest mt-1.5 flex items-center gap-2">
                        {cls.major?.name || 'Reguler'}
                      </div>
                    </td>
                    <td className="p-8">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-black ${
                            isFull
                              ? 'bg-rose-100 text-rose-600'
                              : 'bg-emerald-100 text-emerald-700'
                          }`}
                        >
                          {count} / {maxCap} Siswa
                        </span>
                        {isFull && (
                          <span className="text-[9px] font-black uppercase text-rose-500 tracking-wider">
                            Penuh
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-8">
                      <div className="text-[10px] font-black text-slate-500 uppercase flex items-center gap-1.5">
                        <Building2 className="w-3 h-3 text-slate-300" /> {cls.branch_id}
                      </div>
                    </td>
                    <td className="p-8 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openStudentModal(cls)}
                          className="px-4 py-2 bg-indigo-50 text-indigo-600 hover:bg-indigo-600 hover:text-white rounded-xl transition-all text-xs font-black flex items-center gap-1.5"
                        >
                          <Users className="w-3.5 h-3.5" /> Kelola Siswa
                        </button>
                        <button
                          onClick={() => handleDelete(cls.id)}
                          className="p-2.5 bg-rose-50 text-rose-300 hover:text-rose-600 hover:bg-rose-100 rounded-xl transition-all shadow-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {classes.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="p-24 text-center">
                    <div className="flex flex-col items-center">
                      <LayoutGrid className="w-16 h-16 text-slate-100 mb-4" />
                      <p className="text-slate-300 font-black uppercase tracking-[0.3em] text-xs">
                        Belum Ada Kelas Terdaftar
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODAL KELOLA SISWA */}
      {selectedClass && (
        <div className="fixed inset-0 bg-slate-900/60 z-[150] flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[3rem] w-full max-w-3xl p-8 shadow-2xl relative animate-fade-in-up border border-white max-h-[90vh] flex flex-col">
            <button
              onClick={() => setSelectedClass(null)}
              className="absolute top-6 right-6 p-2 text-slate-400 hover:text-slate-900 transition-all hover:bg-slate-50 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="mb-6">
              <h3 className="font-black text-xl uppercase tracking-tight text-slate-900">
                Kelola Anggota: {selectedClass.name}
              </h3>
              <p className="text-xs text-slate-400 font-bold uppercase mt-1">
                Kapasitas: {classStudents.length} / {selectedClass.max_capacity || 15} Siswa
              </p>
            </div>

            {loadingStudents ? (
              <div className="py-20 flex justify-center items-center">
                <Loader2 className="animate-spin text-indigo-600 w-8 h-8" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 overflow-hidden flex-1">
                {/* Siswa Terdaftar */}
                <div className="flex flex-col border border-slate-100 rounded-2xl p-4 bg-slate-50/50">
                  <h4 className="font-black text-xs uppercase tracking-wider text-slate-700 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-600" /> Siswa Terdaftar ({classStudents.length})
                  </h4>
                  <div className="overflow-y-auto space-y-2 pr-1 flex-1 max-h-[350px]">
                    {classStudents.map((st) => (
                      <div key={st.id} className="p-3 bg-white rounded-xl border border-slate-100 flex items-center justify-between text-xs font-bold shadow-sm">
                        <div>
                          <p className="text-slate-800 uppercase">{st.name}</p>
                          <p className="text-[10px] text-slate-400">{st.grade || 'Tidak ada jenjang'}</p>
                        </div>
                        <button
                          onClick={() => removeStudentFromClass(st.id)}
                          className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                          title="Keluarkan dari kelas"
                        >
                          <UserMinus className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {classStudents.length === 0 && (
                      <p className="text-center text-xs text-slate-400 py-10 uppercase font-bold">Belum Ada Siswa</p>
                    )}
                  </div>
                </div>

                {/* Siswa Belum Punya Kelas */}
                <div className="flex flex-col border border-slate-100 rounded-2xl p-4 bg-slate-50/50">
                  <h4 className="font-black text-xs uppercase tracking-wider text-slate-700 mb-3 flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-emerald-600" /> Siswa Belum Ada Kelas
                  </h4>
                  <div className="overflow-y-auto space-y-2 pr-1 flex-1 max-h-[350px]">
                    {availableStudents.map((st) => (
                      <div key={st.id} className="p-3 bg-white rounded-xl border border-slate-100 flex items-center justify-between text-xs font-bold shadow-sm">
                        <div>
                          <p className="text-slate-800 uppercase">{st.name}</p>
                          <p className="text-[10px] text-slate-400">{st.grade || 'Tidak ada jenjang'}</p>
                        </div>
                        <button
                          disabled={classStudents.length >= (selectedClass.max_capacity || 15)}
                          onClick={() => assignStudentToClass(st.id)}
                          className="p-1.5 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Tambahkan ke kelas"
                        >
                          <UserPlus className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {availableStudents.length === 0 && (
                      <p className="text-center text-xs text-slate-400 py-10 uppercase font-bold">Tidak ada siswa tanpa kelas</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL BUAT KELAS */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-slate-900/60 z-[150] flex items-center justify-center p-4 backdrop-blur-md">
          <div className="bg-white rounded-[3.5rem] w-full max-w-xl p-10 shadow-2xl relative animate-fade-in-up border border-white max-h-[90vh] overflow-y-auto no-scrollbar">
            <button
              onClick={() => setIsFormOpen(false)}
              className="absolute top-8 right-8 p-2 text-slate-400 hover:text-slate-900 transition-all hover:bg-slate-50 rounded-full"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center mb-10">
              <div className="w-16 h-16 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                <Plus className="w-8 h-8" />
              </div>
              <h3 className="font-black text-2xl uppercase tracking-tighter text-slate-900 leading-none">
                Setup Kelas Baru
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">
                Dukung Paralel & Multi Cabang
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                  Penempatan Cabang
                </label>
                <select
                  className="w-full border-2 border-slate-200 p-4 rounded-2xl font-black bg-white text-slate-900 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all text-xs cursor-pointer"
                  value={formData.branch_id}
                  onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                  required
                >
                  <option value="" className="text-slate-400">-- PILIH UNIT CABANG --</option>
                  {branches
                    .filter((b) => b.is_active !== false || b.id === formData.branch_id)
                    .map((b) => (
                      <option key={b.id} value={b.id} className="text-slate-900 font-bold">
                        {b.name.toUpperCase()}
                        {b.is_active === false ? ' (NONAKTIF)' : ''}
                      </option>
                    ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                    Pilih Jenjang
                  </label>
                  <select
                    className="w-full border-2 border-slate-200 p-4 rounded-2xl font-black bg-white text-slate-900 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all text-xs cursor-pointer"
                    value={formData.grade_level_id}
                    onChange={(e) => setFormData({ ...formData, grade_level_id: e.target.value })}
                    required
                  >
                    <option value="" className="text-slate-400">-- PILIH JENJANG --</option>
                    {grades.map((g) => (
                      <option key={g.id} value={g.id} className="text-slate-900 font-bold">
                        KELAS {g.level}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                    Jurusan (Opsional)
                  </label>
                  <select
                    className="w-full border-2 border-slate-200 p-4 rounded-2xl font-black bg-white text-slate-900 outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all text-xs cursor-pointer"
                    value={formData.major_id}
                    onChange={(e) => setFormData({ ...formData, major_id: e.target.value })}
                  >
                    <option value="" className="text-slate-400">-- PILIH JURUSAN --</option>
                    {majors.map((m) => (
                      <option key={m.id} value={m.id} className="text-slate-900 font-bold">
                        {m.name} ({m.code})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                    Nomor Paralel
                  </label>
                  <input
                    type="number"
                    min="1"
                    placeholder="Kosongkan jika Reguler"
                    className="w-full border-2 border-slate-200 p-4 rounded-2xl font-black bg-white text-slate-900 outline-none text-xs"
                    value={formData.parallel ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        parallel: e.target.value ? Number(e.target.value) : null,
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                    Batas Maksimal Siswa
                  </label>
                  <input
                    type="number"
                    min="1"
                    className="w-full border-2 border-slate-200 p-4 rounded-2xl font-black bg-white text-slate-900 outline-none text-xs"
                    value={formData.max_capacity}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        max_capacity: Number(e.target.value) || 15,
                      })
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-indigo-600 uppercase ml-2 tracking-widest">
                    Preview Nama
                  </label>
                  <input
                    readOnly
                    className="w-full border-2 border-indigo-100 p-4 rounded-2xl font-black bg-indigo-50 text-indigo-700 outline-none text-xs uppercase"
                    value={formData.name}
                  />
                </div>
              </div>

              <div className="pt-4 bg-indigo-50/50 p-6 rounded-[2rem] border border-indigo-100">
                <div className="flex gap-3 items-start">
                  <Info className="w-5 h-5 text-indigo-600 shrink-0" />
                  <p className="text-[10px] font-medium text-indigo-900 leading-relaxed uppercase">
                    Maksimal siswa diset ke 15 secara default. Penambahan siswa akan dibatasi saat batas tercapai.
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-indigo-600 text-white py-6 rounded-[2.5rem] font-black uppercase tracking-[0.2em] shadow-2xl hover:bg-indigo-700 transition-all flex justify-center items-center gap-3 active:scale-95 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 className="animate-spin w-5 h-5" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                KONFIRMASI BUAT KELAS
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
